import React, { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { notifyPendingChanged } from '../hooks/usePendingSummary';
import { useAuth } from '../contexts/AuthContext';
import api, { isAuthRejection, isNetworkShapedError } from '../lib/api';
import { IS_APP_BUILD } from '../lib/buildTarget';
import { isCopiedToDevice } from '../lib/offline/deliveryState';
import { useLocalPresence } from '../hooks/useLocalPresence';
import { log as diagLog } from '../lib/diag/readyChainLog';
import { useTranslation } from 'react-i18next';

/**
 * NotificationListener — App-level processing-status toast emitter.
 *
 * Mounts above <Routes /> so toasts appear on ANY route (per design Decision #27).
 * Polls GET /documents every 5s ONLY when there are processing docs AND a user is
 * authenticated. Stops polling otherwise.
 *
 * Lifted from Dashboard.js: the three transition toasts (processing→ready / partial / error).
 * Dashboard still polls independently (every 3s) for the local progress bar UI; the toast
 * emission lives here so it survives route changes (Study, Settings, Pricing, etc.).
 *
 * Chunked docs (those with parent_doc_id): defer per-chunk toasts. Emit a single aggregate
 * toast once ALL siblings in the parent_doc_id group reach a terminal status. Per design
 * doc Section 7.6.
 *
 * Other notifications (welcome, credit-low, conversion prompts, etc.) remain in their
 * original components.
 */
export default function NotificationListener() {
  const { user } = useAuth();
  const { t } = useTranslation(['dashboard', 'common']);

  // Last-seen docs map keyed by doc id (status, filename, question_count, flashcard_count,
  // parent_doc_id, total_chunks). Held in a ref so polling re-fetch doesn't cause re-render.
  const lastSeenRef = useRef(new Map());
  // Parent_doc_ids we've already emitted an aggregate toast for — prevents double-fire on
  // subsequent polls when sibling docs are still in the list.
  const notifiedParentsRef = useRef(new Set());
  // Per-chunk-doc-ids we've already notified about (non-aggregate path safety net) —
  // even within a chunked group we never want the same doc-id toast twice.
  const notifiedDocsRef = useRef(new Set());
  const pollingRef = useRef(null);
  // TANDA 14 (#188) — ¿la falla de red EN CURSO ya se apuntó? Gobierna sólo el
  // throttle de la línea del visor; no toca una decisión del poller. Se re-arma
  // con cada sondeo que sale bien. Va en ref y no en estado a propósito: el
  // visor jamás re-rendea a nadie.
  const falloDeRedApuntadoRef = useRef(false);
  const tRef = useRef(t);
  useEffect(() => { tRef.current = t; }, [t]);

  /* ── #412 (LISTO-LOCAL B3) — EL AVISO ES DEL ATERRIZAJE, NO DEL SERVIDOR ──
     Con #412 la tarjeta de un documento servible que todavía no está copiado se
     pinta como procesando. Un toast diciendo «¡X está listo! 42 preguntas» en ese
     mismo instante lo contradiría —y mandaría al usuario a una pantalla que le va
     a decir que el material no está en su aparato—. Así que en la app el aviso se
     RETIENE y sale, UNA sola vez, cuando el cartero avisa que esa fila aterrizó.
     No cuelga del sondeo (que se apaga en cuanto no queda nada cocinando): cuelga
     del evento post-commit, que es el hecho de verdad. Si el material YA estaba
     copiado al ver el `ready` —el caso raro— el aviso sale como hoy, y en web el
     camino entero es el de siempre. */
  const { presenceById } = useLocalPresence();
  const presenciaRef = useRef(presenceById);
  useEffect(() => { presenciaRef.current = presenceById; }, [presenceById]);
  // clave -> { faltan: Set<id de documento>, emitir: () => void }
  const retenidosRef = useRef(new Map());

  // F6.7a — los invitados YA NO salen temprano. Su exclusión era el hueco más
  // duro de la entrega: sin poller no había `docs-changed`, sin `docs-changed`
  // el cartero no despertaba, y el material de un invitado solo bajaba en el
  // siguiente arranque. Ahora recorren el MISMO camino de datos que todos, con
  // lo VISUAL silenciado: cero toasts nuevos, cero badge nuevo, cero cambios de
  // permisos o autenticación. Lo único que ganan es que su material llegue.
  useEffect(() => {
    if (!user) {
      // No polling for unauthenticated users. Reset state so a future login starts clean.
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      lastSeenRef.current.clear();
      notifiedParentsRef.current.clear();
      notifiedDocsRef.current.clear();
      return;
    }

    const isGuest = Boolean(user.is_guest);
    let cancelled = false;

    // F6.7a — TRAS CADA resultado del poller, haya visto transición o no. Ese
    // "o no" es el corazón del sprint: si el servidor terminó de procesar entre
    // dos sondeos, la transición processing→ready NUNCA se observa y el camino
    // viejo (solo emitir en transición) no despertaba a nadie. La convergencia
    // no necesita ver el cambio: compara estados y le basta con el resultado.
    // Reusa el payload que el poll ACABA de traer → cero llamadas extra.
    const convergeAfterPoll = (docs) => {
      if (!IS_APP_BUILD) return;
      import('../lib/warehouse/converge')
        .then((c) => c.convergeWarehouse(c.CONVERGE_REASON.POLL_RESULT, { documents: docs }))
        .catch(() => {});
    };

    // #412 — ¿este documento ya está de verdad en el teléfono? En web la
    // pregunta no existe: siempre sí, y el aviso sale como siempre.
    const enElTelefono = (id) => !IS_APP_BUILD || isCopiedToDevice(presenciaRef.current.get(id));

    /* #412 — emitir ahora, o retener hasta que aterrice. `ids` son los
       documentos de los que ese aviso habla (uno, o todas las partes de un
       grupo); el aviso sale cuando NO falta ninguno. */
    const avisar = (clave, ids, emitir) => {
      const faltan = ids.filter((id) => !enElTelefono(id));
      if (faltan.length === 0) { emitir(); return; }
      retenidosRef.current.set(clave, { faltan: new Set(faltan), emitir });
    };

    /* TANDA 14 (firma #188) — LOS DOS `catch` DEL POLLER DEJAN DE SER MUDOS.
       Se tragaban TODO en silencio, incluida la cadena 401 → refresh fallido: en
       la bitácora del congelón (boot `c86wr6`, 27-ago) el usuario se quedó con
       datos vacíos, el indicador diciendo que había conexión, y ni un rastro que
       leer después. El fallo existió y nadie pudo verlo.

       LO QUE ESTO NO HACE, Y ES LA MITAD IMPORTANTE: el poller NO decide sesión.
       Ni logout, ni expulsión a login. Esa decisión ya vive en el interceptor de
       `lib/api.js` bajo la regla F6.α —red ≠ token inválido—, con el contexto del
       refresh que aquí no existe. Duplicarla sería tener dos jueces para el mismo
       hecho, y el segundo a ciegas. Aquí sólo se apunta lo que se vio.

       LA CLASIFICACIÓN sale de los dos helpers de `lib/api.js`, que son la fuente
       única del repo para esas preguntas: aquí no nace ningún criterio nuevo.

       EL THROTTLE lo lleva SÓLO la forma de red: con la señal caída el tick de
       5 s escribiría 12 líneas por minuto y ahogaría el anillo del visor sin
       añadir un dato. Se apunta la PRIMERA falla tras un éxito. El rechazo de
       auth no se acalla: es raro, y cada uno importa.

       Fire-and-forget, como todo emisor del visor: su propio fallo no puede
       tocar un sondeo. Lo fija `NotificationListener.tanda14.test.js`. */
    const registrarFalloPoll = (err, origen) => {
      if (!IS_APP_BUILD) return;
      try {
        if (isAuthRejection(err)) {
          diagLog('DOCS.POLL', { forma: 'auth', status: err?.response?.status, origen });
          return;
        }
        if (isNetworkShapedError(err)) {
          if (falloDeRedApuntadoRef.current) return;
          falloDeRedApuntadoRef.current = true;
          diagLog('DOCS.POLL', { forma: 'red', origen });
          return;
        }
        // Ni sesión ni red: un 4xx real del endpoint. Los dos helpers son
        // deliberadamente NO exhaustivos (lo dice su propio letrero), así que
        // este tercer caso existe — y dejarlo mudo sería reabrir el hueco en
        // chiquito. Es raro por construcción: se apunta siempre.
        diagLog('DOCS.POLL', { forma: 'otro', status: err?.response?.status ?? null, origen });
      } catch (e) { /* el visor jamás decide un sondeo */ }
    };

    const pollOnce = async () => {
      try {
        const res = await api.get('/documents');
        // El sondeo salió: el aviso de forma-de-red vuelve a estar armado.
        falloDeRedApuntadoRef.current = false;
        const docs = res.data?.documents || [];
        if (cancelled) return;

        const prev = lastSeenRef.current;
        const next = new Map();
        const transitionedDocs = [];   // docs that just transitioned to a terminal status

        for (const doc of docs) {
          next.set(doc.id, doc);
          const old = prev.get(doc.id);
          const wasProcessing = old?.status === 'processing';
          const isTerminal = ['ready', 'partial', 'error'].includes(doc.status);
          if (wasProcessing && isTerminal && !notifiedDocsRef.current.has(doc.id)) {
            transitionedDocs.push(doc);
            notifiedDocsRef.current.add(doc.id);
          }
        }
        lastSeenRef.current = next;

        // Group transitioned docs by parent_doc_id (chunked) vs none (standalone).
        // Toast emission is VISUAL: silenciada para invitados (F6.7a) — el
        // camino de DATOS de abajo corre igual para ellos.
        const parentsSeenThisTick = new Set();
        for (const doc of isGuest ? [] : transitionedDocs) {
          if (doc.parent_doc_id) {
            parentsSeenThisTick.add(doc.parent_doc_id);
            // A failed split part now behaves like a normal failed doc (status 'error', not
            // demoted to pending) → give it the SAME explicit failure toast. The aggregate
            // "X of Y ready" toast below alone would not tell the user a part failed.
            if (doc.status === 'error') {
              toast.error(tRef.current('dashboard:toasts.processing_failed', { filename: doc.filename }));
            }
            continue;  // ready/partial chunked parts → handled in aggregate pass below
          }
          // Standalone doc — emit per-doc toast immediately
          // #412: «inmediatamente» sigue siendo cierto en web y con el material ya
          // copiado; si falta bajarlo, el aviso espera al aterrizaje.
          if (doc.status === 'ready') {
            avisar(doc.id, [doc.id], () => toast.success(tRef.current('dashboard:toasts.doc_ready', { filename: doc.filename, count: doc.question_count })));
          } else if (doc.status === 'partial') {
            const toastKey = doc.question_count === 0
              ? 'dashboard:toasts.doc_partial_questions_failed'
              : 'dashboard:toasts.doc_partial_flashcards_failed';
            avisar(doc.id, [doc.id], () => toast.warning(tRef.current(toastKey, { filename: doc.filename })));
          } else if (doc.status === 'error') {
            toast.error(tRef.current('dashboard:toasts.processing_failed', { filename: doc.filename }));
          }
        }

        // A chunked part just reached a terminal status -> pending-summary consumers
        // (sidebar/bottom-nav badge, dashboard banner, /pending parts map) refresh NOW.
        if (parentsSeenThisTick.size > 0) notifyPendingChanged();

        // F4.3b T4: el MISMO hecho que emite el toast "está listo" actualiza las
        // listas visibles. El payload del poll ya trae los documentos COMPLETOS,
        // así que viaja en el detail y los consumidores montados (Centro de
        // Estudio) lo usan tal cual — CERO llamadas extra. Dashboard conserva su
        // listener de siempre (refetch silencioso: también refresca créditos).
        // screenDataCache invalida las listas cacheadas aunque nadie esté montado.
        if (transitionedDocs.length > 0) {
          window.dispatchEvent(new CustomEvent('studybuddy:docs-changed', { detail: { documents: docs } }));
        }

        // F6.7a — la convergencia corre SIEMPRE, con transición o sin ella.
        // Va DESPUÉS del despacho de docs-changed a propósito: ese camino ya
        // despierta al cartero por sí solo y el coalescing del downloader
        // convierte las dos llamadas en una corrida más, nunca en dos.
        convergeAfterPoll(docs);

        // Aggregate toast for chunked parents whose siblings are all terminal
        for (const parentId of parentsSeenThisTick) {
          if (notifiedParentsRef.current.has(parentId)) continue;
          const siblings = docs.filter(d => d.parent_doc_id === parentId);
          if (siblings.length === 0) continue;
          const allTerminal = siblings.every(d => ['ready', 'partial', 'error'].includes(d.status));
          if (!allTerminal) continue;
          const readyCount = siblings.filter(d => d.status === 'ready').length;
          const totalQuestions = siblings.reduce((s, d) => s + (d.question_count || 0), 0);
          const totalFlashcards = siblings.reduce((s, d) => s + (d.flashcard_count || 0), 0);
          // #412: el agregado habla de TODAS las partes, así que espera a que
          // todas las servibles hayan aterrizado. Las que fallaron no bajan nada
          // y por eso no cuentan.
          const idsServibles = siblings.filter(d => d.status !== 'error').map(d => d.id);
          avisar(`parent:${parentId}`, idsServibles, () => toast.success(tRef.current('dashboard:toasts.chunks_ready', {
            count: siblings.length,
            ready: readyCount,
            total: siblings.length,
            questions: totalQuestions,
            flashcards: totalFlashcards,
          })));
          notifiedParentsRef.current.add(parentId);
        }

        // Manage polling cadence: keep polling while any doc is still processing
        // LISTO-LOCAL B2: la cadencia pasa a ser UNA regla, no dos mitades. La
        // de apagar es la de siempre, byte por byte; la de encender es la misma
        // que usa el arranque y el payload de `docs-changed`, y aquí es un
        // no-op (el intervalo está vivo) salvo que alguien lo hubiera apagado
        // entre dos ticks.
        const anyStillProcessing = docs.some(d => d.status === 'processing');
        if (!anyStillProcessing && pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        } else {
          armarSondeoSiProcesa(docs);
        }
      } catch (err) {
        // TANDA 14 (#188): ya no es silencio — se apunta y se sigue. El
        // siguiente tick reintenta, exactamente como antes.
        registrarFalloPoll(err, 'poll');
      }
    };

    /* ── LISTO-LOCAL B2 — LA REGLA DE ARMADO, UNA SOLA Y PARA CUALQUIER LISTA ──
       El sondeo de 5 s se apaga solo en cuanto ningún documento está
       `processing` (abajo, en la cadencia de `pollOnce`), y hasta hoy lo
       revivía UNA sola cosa: `studybuddy:doc-uploaded`. Cualquier otro modo de
       poner un documento en proceso se quedaba sin quien lo sondeara — y con el
       sondeo muerto tampoco corre `convergeAfterPoll`, así que el material no se
       pide al terminar y el aviso de «ya está listo» no sale.

       Ahora la regla es la misma para las TRES listas que este poller ve: la del
       arranque, la de cada tick y la que viaja en el payload de `docs-changed`.
       Idempotente por construcción (`if (pollingRef.current) return`), así que
       una ráfaga de eventos no apila intervalos.

       SIN LISTA NO SE ADIVINA NADA: un `docs-changed` sin payload no arma, no
       pide y no cambia nada — quien lo despacha (borrado, edición de
       flashcards) no pone documentos a cocinar. */
    const armarSondeoSiProcesa = (lista) => {
      if (pollingRef.current) return;
      if (!Array.isArray(lista)) return;
      if (!lista.some(d => d && d.status === 'processing')) return;
      pollingRef.current = setInterval(pollOnce, 5000);
    };

    // Bootstrap: prime lastSeen with current doc statuses (so we don't re-toast existing terminal docs),
    // then start polling if any are processing.
    (async () => {
      try {
        const res = await api.get('/documents');
        // Mismo re-armado que el tick: un sondeo que sale bien cierra el
        // episodio de red anterior, venga del camino que venga.
        falloDeRedApuntadoRef.current = false;
        if (cancelled) return;
        const docs = res.data?.documents || [];
        for (const doc of docs) {
          lastSeenRef.current.set(doc.id, doc);
          if (['ready', 'partial', 'error'].includes(doc.status)) {
            notifiedDocsRef.current.add(doc.id);  // suppress retro-toast for pre-existing terminal docs
          }
        }
        // LISTO-LOCAL B2: la MISMA regla de arriba, con la lista del arranque.
        // Byte por byte lo que este bloque ya hacía.
        armarSondeoSiProcesa(docs);
      } catch (err) {
        // TANDA 14 (#188): el gemelo del catch de arriba. Este camino es el que
        // más callaba: si el bootstrap falla, el intervalo ni se arma, así que
        // el «next tick will retry» de al lado no tenía ningún tick que dar.
        registrarFalloPoll(err, 'bootstrap');
      }
    })();

    // Re-arm polling when a "doc-uploaded" custom event fires — Dashboard dispatches this
    // when new docs are created (regular upload or finalize-split). Robust against the
    // case where polling stopped because no docs were processing, then a new upload starts.
    const onDocUploaded = () => {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(pollOnce, 5000);
      }
    };
    document.addEventListener('studybuddy:doc-uploaded', onDocUploaded);

    // LISTO-LOCAL B2 — y la LISTA, venga de donde venga. `docs-changed` es el
    // aviso de la casa de que la biblioteca cambió y muchas veces trae la lista
    // fresca dentro; cuando la trae, esta es la tercera y última puerta de la
    // misma regla. Cero peticiones: se decide con lo que el evento ya carga.
    const onDocsChanged = (e) => { armarSondeoSiProcesa(e?.detail?.documents); };
    window.addEventListener('studybuddy:docs-changed', onDocsChanged);

    // #412 — el aterrizaje suelta los avisos retenidos. El cartero emite UNA
    // vez por corrida con los ids que commiteó; un aviso sale cuando ya no le
    // falta ninguno de los suyos, y sale UNA sola vez (se borra al emitirlo).
    const alAterrizar = (e) => {
      const ids = e?.detail?.documentIds;
      if (!Array.isArray(ids) || ids.length === 0) return;
      for (const [clave, entrada] of Array.from(retenidosRef.current.entries())) {
        for (const id of ids) entrada.faltan.delete(id);
        if (entrada.faltan.size > 0) continue;
        retenidosRef.current.delete(clave);
        try { entrada.emitir(); } catch (err) { /* un aviso roto no calla a los demás */ }
      }
    };
    window.addEventListener('studybuddy:local-material-ready', alAterrizar);

    return () => {
      cancelled = true;
      if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
      document.removeEventListener('studybuddy:doc-uploaded', onDocUploaded);
      window.removeEventListener('studybuddy:docs-changed', onDocsChanged);
      window.removeEventListener('studybuddy:local-material-ready', alAterrizar);
    };
  }, [user]);

  return null;  // listener-only, renders nothing
}
